// ─── Section-v1 Layout Utilities ──────────────────────────────
// Phase 1: percentage + anchor positioning for the public renderer.
//
// Data shape (additive — coexists with legacy x/y/w):
//   element.layout[viewportKey] = {
//     x, y, w,                     // legacy 0–1 (source of truth in Phase 1)
//     xPercent, yPercent,          // 0–100  (derived)
//     widthPercent,                // 0–100  (derived)
//     anchor,                      // 'tl'|'t'|'tr'|'l'|'c'|'r'|'bl'|'b'|'br' (default 'c')
//   }
//   element.parentId = null         // future use (groups) — null = direct section child
//
// Section-level: section._aspectRatio = sectionHeightPx / REF_WIDTH
// We render the section as `width: 100%; height: aspectRatio * width`
// so that the *percentage* coordinates remain visually proportional
// across viewport widths.

export const SECTION_V1_REF_WIDTH = 390;

// Translate-percent values for each anchor — same convention as CSS transform.
// We pin (xPercent, yPercent) of the SECTION to this corner of the ELEMENT.
const ANCHOR_TRANSFORM = {
  tl: { tx: 0,    ty: 0 },
  t:  { tx: -50,  ty: 0 },
  tr: { tx: -100, ty: 0 },
  l:  { tx: 0,    ty: -50 },
  c:  { tx: -50,  ty: -50 },
  r:  { tx: -100, ty: -50 },
  bl: { tx: 0,    ty: -100 },
  b:  { tx: -50,  ty: -100 },
  br: { tx: -100, ty: -100 },
};

function clamp(v, lo, hi) {
  if (typeof v !== 'number' || Number.isNaN(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Resolve the section-v1 position for an element layout entry.
 * Falls back gracefully to legacy x/y/w if percent fields are missing.
 * Always returns numbers in % space so the caller can render directly.
 */
export function resolveElementV1Position(layoutEntry) {
  const src = layoutEntry || {};
  // Prefer the explicit percent fields (kept in sync by the backend save adapter)
  let xPercent = src.xPercent;
  let yPercent = src.yPercent;
  let widthPercent = src.widthPercent;
  // Fallback: derive from legacy 0–1 fields
  if (typeof xPercent !== 'number') xPercent = (src.x ?? 0.5) * 100;
  if (typeof yPercent !== 'number') yPercent = (src.y ?? 0.5) * 100;
  if (typeof widthPercent !== 'number') widthPercent = (src.w ?? 0.3) * 100;
  // Anchor — default 'c' (center) so unmigrated elements behave exactly like the legacy renderer.
  const anchor = ANCHOR_TRANSFORM[src.anchor] ? src.anchor : 'c';
  return {
    xPercent: clamp(xPercent, 0, 100),
    yPercent: clamp(yPercent, 0, 100),
    widthPercent: clamp(widthPercent, 4, 100),
    anchor,
  };
}

/**
 * Build the CSS style object for a section-v1 positioned element.
 * Uses % positioning + anchor-aware translate. Height is auto so the
 * natural element renders (preserving image/text intrinsic height).
 */
export function getElementV1Style(layoutEntry, zIndex = 1) {
  const { xPercent, yPercent, widthPercent, anchor } = resolveElementV1Position(layoutEntry);
  const { tx, ty } = ANCHOR_TRANSFORM[anchor] || ANCHOR_TRANSFORM.c;
  return {
    position: 'absolute',
    left: `${xPercent}%`,
    top: `${yPercent}%`,
    width: `${widthPercent}%`,
    transform: `translate(${tx}%, ${ty}%)`,
    zIndex,
  };
}

/**
 * Pick the right layout entry for the current viewport: mobile vs desktop,
 * with safe fallback when only one is defined.
 */
export function pickV1LayoutForViewport(elementLayout, isMobile) {
  if (!elementLayout) return null;
  if (isMobile) {
    return elementLayout.mobile || elementLayout.desktop || null;
  }
  return elementLayout.desktop || elementLayout.mobile || null;
}

/**
 * Compute the section aspect ratio (legacy entry point — kept for callers
 * that only need the aspect number). Internally delegates to
 * `resolveSectionLayout` so the canonical-rules are enforced once.
 */
export function resolveSectionAspectRatio(section, sectionData, isMobile) {
  return resolveSectionLayout(section, sectionData, isMobile).aspectRatio;
}

// ─── Phase 1+2 Unified resolvers ───────────────────────────────
// One source of truth for section + element bounds. Used by:
//   • CollageCanvas (editor)         – `section` style + element wrapper
//   • SectionV1CollageCanvas (public)– `section` style + element wrapper
//   • LayoutDebugOverlay             – reports the exact resolved values
//   • mobileLayoutHealthCheck        – uses resolved bounds for out-of-bounds detection
//   • renderParityGuard              – compares same canonical values across renderers
//
// Rules enforced here (the "canonical" rules):
//   1. Live `section.height.mobile|desktop` wins over any stamped _aspectRatio.
//   2. Percent fields and legacy x/y/w are kept in sync; if they disagree
//      by more than CONFLICT_TOLERANCE we flag a `conflict` so the debug
//      overlay can show a warning — drift is visible the moment it appears.
//   3. Renderer prefers percent fields (matches the saved/normalized data
//      shape) but falls back to legacy without surprise.

export const CONFLICT_TOLERANCE = 0.5; // percentage-point tolerance for legacy vs percent

/**
 * Resolve canonical section layout. Returns the values that every renderer
 * SHOULD use for the section element.
 *
 * `aspectRatio` = sectionHeight / sectionWidth (i.e. legacy interpretation
 * used elsewhere in the codebase). The CSS aspect-ratio property wants
 * width/height, so the renderer applies `1/aspectRatio`.
 *
 * `source` tells us where the height came from — useful in the debug
 * overlay to spot stale stamps.
 */
export function resolveSectionLayout(section, sectionData, isMobile) {
  const livePx = isMobile
    ? section?.height?.mobile
    : section?.height?.desktop;
  if (typeof livePx === 'number' && livePx > 0) {
    return {
      heightPx: livePx,
      widthBasisPx: SECTION_V1_REF_WIDTH,
      aspectRatio: livePx / SECTION_V1_REF_WIDTH,
      source: 'live-section-height',
    };
  }
  const stamped = sectionData?._aspectRatio ?? section?._aspectRatio;
  if (typeof stamped === 'number' && stamped > 0) {
    return {
      heightPx: stamped * SECTION_V1_REF_WIDTH,
      widthBasisPx: SECTION_V1_REF_WIDTH,
      aspectRatio: stamped,
      source: 'stamped-aspect-ratio',
    };
  }
  const fallbackPx = isMobile ? 800 : 900;
  return {
    heightPx: fallbackPx,
    widthBasisPx: SECTION_V1_REF_WIDTH,
    aspectRatio: fallbackPx / SECTION_V1_REF_WIDTH,
    source: 'default-fallback',
  };
}

/**
 * Resolve canonical element bounds for the given viewport.
 *
 * Returns the % values every renderer should use AND flags any conflict
 * between legacy (x/y/w) and percent (xPercent/yPercent/widthPercent)
 * fields. Renderers should consume `xPercent`/`yPercent`/`widthPercent`;
 * the debug overlay and parity guard can additionally read `conflicts`.
 *
 * `anchor` defaults to 'c' so unmigrated elements render exactly like the
 * legacy "translate(-50%, -50%)" math.
 */
export function resolveElementBounds(element, isMobile) {
  const layout = element?.layout || null;
  const entry = layout ? (isMobile ? (layout.mobile || layout.desktop) : (layout.desktop || layout.mobile)) : null;
  const src = entry || {};

  // Detect each axis's source and any drift.
  const conflicts = [];
  const resolveAxis = (legacyKey, percentKey, defaultLegacy, lo, hi) => {
    const legacy = src[legacyKey];
    const percent = src[percentKey];
    const hasPercent = typeof percent === 'number' && !Number.isNaN(percent);
    const hasLegacy = typeof legacy === 'number' && !Number.isNaN(legacy);
    let value;
    let source;
    if (hasPercent) {
      value = percent;
      source = 'percent';
      if (hasLegacy) {
        const derivedFromLegacy = legacy * 100;
        if (Math.abs(derivedFromLegacy - percent) > CONFLICT_TOLERANCE) {
          conflicts.push({
            axis: legacyKey,
            legacy: legacy,
            legacyAsPercent: +derivedFromLegacy.toFixed(2),
            percent: +percent.toFixed(2),
            delta: +(derivedFromLegacy - percent).toFixed(2),
          });
        }
      }
    } else if (hasLegacy) {
      value = legacy * 100;
      source = 'legacy';
    } else {
      value = defaultLegacy * 100;
      source = 'default';
    }
    return { value: clamp(value, lo, hi), source };
  };

  const x = resolveAxis('x', 'xPercent', 0.5, 0, 100);
  const y = resolveAxis('y', 'yPercent', 0.5, 0, 100);
  const w = resolveAxis('w', 'widthPercent', 0.3, 4, 100);
  const anchor = ANCHOR_TRANSFORM[src.anchor] ? src.anchor : 'c';

  return {
    xPercent: x.value,
    yPercent: y.value,
    widthPercent: w.value,
    anchor,
    sources: { x: x.source, y: y.source, w: w.source },
    conflicts,         // [] when clean
    hasConflict: conflicts.length > 0,
    z: typeof src.z === 'number' ? src.z : 1,
  };
}

/**
 * Compute pixel bounds for an element inside a given section box.
 * Pure math — no DOM, no React. Used by the mobile health check, parity
 * guard, and debug overlay so they all evaluate elements with identical
 * geometry to what the renderer produces.
 *
 * `sectionBox` = { widthPx, heightPx } — the actual rendered section
 * dimensions (may differ from canonical when container width != REF_WIDTH).
 */
export function computeElementPixelBox(bounds, sectionBox, intrinsicAspect) {
  const { xPercent, yPercent, widthPercent, anchor } = bounds;
  const { widthPx, heightPx } = sectionBox;
  const w = (widthPercent / 100) * widthPx;
  // For elements with no fixed height (images/text/shapes-with-aspect-ratio),
  // the rendered height is governed by intrinsic aspect of inner content.
  // The renderer applies `aspectRatio` to the inner div; for the resolver's
  // purposes we accept an optional `intrinsicAspect` (= inner W/H) and use
  // it to compute height. When unknown, treat as a square (1).
  const h = intrinsicAspect && intrinsicAspect > 0 ? w / intrinsicAspect : w;
  const cx = (xPercent / 100) * widthPx;
  const cy = (yPercent / 100) * heightPx;
  const t = ANCHOR_TRANSFORM[anchor] || ANCHOR_TRANSFORM.c;
  const left = cx + (t.tx / 100) * w;
  const top = cy + (t.ty / 100) * h;
  return {
    leftPx: left,
    topPx: top,
    widthPx: w,
    heightPx: h,
    rightPx: left + w,
    bottomPx: top + h,
  };
}
